import { NgxPermissionsService } from 'ngx-permissions';
import { MaybeAsync, Router } from '@angular/router';
import { filter, map, tap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Guard function type for controlling navigation record visibility.
 * Executed within Angular's injection context during navigation compilation.
 *
 * @returns Promise, Observable, or boolean indicating if the record should be displayed
 *
 * @example
 * ```typescript
 * const adminOnlyGuard: ActivatorGuard = () => {
 *   const authService = inject(AuthService);
 *   return authService.hasRole('admin');
 * };
 *
 * const asyncGuard: ActivatorGuard = async () => {
 *   const permissions = await permissionService.getPermissions();
 *   return permissions.includes('view-reports');
 * };
 * ```
 */
export type ActivatorGuard = () => MaybeAsync<boolean>;

/**
 * Configuration interface for the PermissionsGuard.
 * Allows fine-grained control over permission checking logic.
 */
export interface PermissionsGuardArgs {
  /**
   * Array of permission names that the user must NOT have.
   * If the user has any of these permissions, access will be denied.
   * @example ['banned', 'suspended']
   */
  except?: string[];

  /**
   * Array of permission names that the user must have ALL of.
   * If the user lacks any of these permissions, access will be denied.
   * @example ['admin', 'user']
   */
  every?: string[];

  /**
   * Array of permission names that the user must have AT LEAST ONE of.
   * If the user has none of these permissions, access will be denied.
   * @example ['admin', 'moderator', 'user']
   */
  some?: string[];
}

/**
 * Internal function to retrieve and process user permissions from the NgxPermissionsService.
 *
 * This function:
 * 1. Injects the NgxPermissionsService to access current user permissions
 * 2. Maps the permissions records to extract permission names
 * 3. Filters out null/undefined records to prevent runtime errors
 * 4. Filters out empty permission arrays to ensure permissions are loaded
 *
 * @returns An Observable that emits an array of valid permission names
 * @private
 */
const getPermissions = () => {
  const ngxPermissionsService = inject(NgxPermissionsService);
  return ngxPermissionsService.permissions$.pipe(
    map((records) =>
      Object.values(records)
        .filter((record) => record?.name) // Filter out null/undefined records
        .map((record) => record.name),
    ),
    filter((permissions) => permissions.length > 0),
  );
};

/**
 * Type definition for the PermissionsGuard factory function.
 */
type IPermissionsGuard = (args: PermissionsGuardArgs) => ActivatorGuard;

/**
 * Factory function that creates route guards based on user permissions.
 *
 * This guard supports three types of permission checking:
 * - `every`: User must have ALL specified permissions
 * - `some`: User must have AT LEAST ONE of the specified permissions
 * - `except`: User must NOT have ANY of the specified permissions
 *
 * The guard waits for permissions to be loaded before making decisions.
 * If no permission constraints are specified, access is granted by default.
 *
 * @example
 * ```typescript
 * // User must have both 'admin' and 'user' permissions
 * const guard = PermissionsGuard({ every: ['admin', 'user'] });
 *
 * // User must have at least one of 'admin', 'moderator', or 'user'
 * const guard = PermissionsGuard({ some: ['admin', 'moderator', 'user'] });
 *
 * // User must NOT have 'banned' permission
 * const guard = PermissionsGuard({ except: ['banned'] });
 *
 * // Complex scenario: User must have 'admin' AND 'user',
 * // must NOT have 'banned', and can optionally have 'moderator'
 * const guard = PermissionsGuard({
 *   every: ['admin', 'user'],
 *   except: ['banned'],
 *   some: ['moderator', 'superuser']
 * });
 * ```
 *
 * @param args - Configuration object specifying permission requirements
 * @returns A guard function that can be used in Angular route configurations
 */
export const PermissionsGuard: IPermissionsGuard & {
  /**
   * Creates a guard with automatic redirect to '/unauthorized' when access is denied.
   *
   * This variant of the PermissionsGuard automatically navigates users to the
   * unauthorized page when they don't meet the permission requirements.
   *
   * @example
   * ```typescript
   * // Will redirect to '/unauthorized' if user lacks 'admin' permission
   * const guard = PermissionsGuard.withRedirect({ every: ['admin'] });
   * ```
   *
   * @param args - Configuration object specifying permission requirements
   * @returns A guard function that redirects unauthorized users
   */
  withRedirect: IPermissionsGuard;
} = (args) => {
  const guard: ActivatorGuard = () =>
    getPermissions().pipe(
      map((permissions) => {
        const { except, every, some } = args;

        // Check if user has any excluded permissions
        if (except?.length > 0) {
          const hasExceptMatch = except.some((p) => permissions.includes(p));
          if (hasExceptMatch) return false;
        }

        // Check if user has all required permissions
        if (every?.length > 0) {
          const hasEveryMatch = every.every((p) => permissions.includes(p));
          if (!hasEveryMatch) return false;
        }

        // Check if user has at least one of the required permissions
        if (some?.length > 0) {
          const hasSomeMatch = some.some((p) => permissions.includes(p));
          if (!hasSomeMatch) return false;
        }

        return true;
      }),
    );
  return guard;
};

/**
 * Extension of PermissionsGuard that automatically redirects unauthorized users.
 *
 * When access is denied, this guard will automatically navigate the user to
 * the '/unauthorized' route using Angular's Router service.
 *
 * @param args - Configuration object specifying permission requirements
 * @returns A guard function that redirects unauthorized users to '/unauthorized'
 */
PermissionsGuard.withRedirect = (args) => {
  const guard = PermissionsGuard(args);
  return () => {
    const router = inject(Router);

    const observable = guard() as Observable<boolean>;
    return observable.pipe(
      tap((hasAccess) => {
        if (!hasAccess) {
          router.navigate(['/unauthorized']);
        }
      }),
    );
  };
};
